import { z } from "zod";

import {
  PersonMatchRequestSchema,
  type PersonMatchRequest,
  type PersonMatchRequestUnbranded,
} from "@thalamos/haven-common";

/** What the match form holds while somebody fills it in. */
export type MatchPersonValues = {
  searchBy: "demographics" | "upn";
  family: string;
  given: string;
  phoneticFamily: boolean;
  phoneticGiven: boolean;
  dateOfBirthKnown: "" | "yes" | "no";
  dateOfBirth: string;
  upn: string;
};

export const emptyMatchPersonValues = (): MatchPersonValues => ({
  searchBy: "demographics",
  family: "",
  given: "",
  phoneticFamily: true,
  phoneticGiven: true,
  dateOfBirthKnown: "",
  dateOfBirth: "",
  upn: "",
});

const formIssues = (
  values: MatchPersonValues,
): { message: string; path: PropertyKey[] }[] => {
  if (values.searchBy === "upn") {
    return [];
  }

  if (values.dateOfBirthKnown === "") {
    return [
      {
        message: "personMatch.dateOfBirthKnown.required",
        path: ["dateOfBirthKnown"],
      },
    ];
  }

  return values.dateOfBirthKnown === "yes" && values.dateOfBirth === ""
    ? [
        {
          message: "personMatch.dateOfBirth.required",
          path: ["dateOfBirth"],
        },
      ]
    : [];
};

/**
 * Reads the form into the match the server is asked to run, so the form and
 * the server refuse the same things with the same messages. Only the question
 * the request cannot express — whether a date of birth is known — is the
 * form's own.
 */
export const MatchPersonValuesSchema = z
  .object({
    searchBy: z.enum(["demographics", "upn"]),
    family: z.string(),
    given: z.string(),
    phoneticFamily: z.boolean(),
    phoneticGiven: z.boolean(),
    dateOfBirthKnown: z.enum(["", "yes", "no"]),
    dateOfBirth: z.string(),
    upn: z.string(),
  })
  .transform((values, context) => {
    const request: PersonMatchRequestUnbranded =
      values.searchBy === "upn"
        ? { type: "upn", upn: values.upn }
        : {
            type: "demographics",
            family: values.family,
            given: values.given,
            phoneticFamily: values.phoneticFamily,
            phoneticGiven: values.phoneticGiven,
            dateOfBirth:
              values.dateOfBirthKnown === "yes" ? values.dateOfBirth : null,
          };

    const parsed = PersonMatchRequestSchema.safeParse(request);
    const issues = [
      ...formIssues(values),
      ...(parsed.success ? [] : parsed.error.issues),
    ];

    for (const issue of issues) {
      context.addIssue({
        code: "custom",
        message: issue.message,
        path: issue.path,
      });
    }

    return parsed.success && issues.length === 0 ? parsed.data : z.NEVER;
  });

/** The form as it stood when `request` was run, to fill it back in. */
export const valuesFromRequest = (
  request: PersonMatchRequest,
): MatchPersonValues =>
  request.type === "upn"
    ? { ...emptyMatchPersonValues(), searchBy: "upn", upn: request.upn }
    : {
        ...emptyMatchPersonValues(),
        family: request.family,
        given: request.given ?? "",
        phoneticFamily: request.phoneticFamily,
        phoneticGiven: request.phoneticGiven,
        dateOfBirthKnown: request.dateOfBirth === null ? "no" : "yes",
        dateOfBirth: request.dateOfBirth ?? "",
      };

/**
 * The match handed from one page to the next in router state. That state is
 * whatever the browser kept, so it is parsed rather than trusted.
 */
export const readMatchHandOff = (state: unknown): PersonMatchRequest | null => {
  const parsed = PersonMatchRequestSchema.safeParse(state);
  return parsed.success ? parsed.data : null;
};
